import React from "react";
// import { useNavigate } from "react-router-dom";
import "./IndustryExperience.css";

const IndustryExperience = () => {
  // const navigate = useNavigate();

  // const goCounselling = () => {
  //   navigate("/counselling");
  // };

  return (
    <main className="iep-page">

      {/* =====================================================
          HERO
      ===================================================== */}
      <section className="iep-hero">

        <div className="iep-glow iep-glow-pink" />
        <div className="iep-glow iep-glow-blue" />

        <div className="iep-container iep-hero-content">

          <p className="iep-kicker">
            INDUSTRY EXPERIENCE PROGRAM
          </p>

          <h1>
            You Know Coding & AI.
            <span> But Where Is the Industry Experience?</span>
          </h1>

          <p className="iep-hero-description">
            You already learned coding, AI or software skills. Now the missing proof is
            <strong> professional execution </strong> — real requirements, deadlines,
            teamwork, reviews, GitHub delivery, debugging, documentation and interview-ready
            project stories. This program is built to close that gap.
          </p>

          <div className="iep-hero-points">
            <span>Real Industry Projects</span>
            <span>Professional Work Environment</span>
            <span>Experience Building</span>
            <span>Interview Preparation</span>
            <span>Opportunity Search</span>
          </div>

          

        </div>

      </section>

      <section className="iep-momentum-strip">
        <div className="iep-container">
          <div className="iep-momentum-head">
            <p className="iep-kicker">FROM SKILL TO PROFESSIONAL PROOF</p>
            <h2>Stop collecting tutorials. Start building evidence that you can work.</h2>
          </div>

          <div className="iep-momentum-grid">
            {[
              ["01", "Skill Check", "Understand what you already know and what must be strengthened before project execution."],
              ["02", "Real Project Work", "Work through requirements, tasks, GitHub, debugging, reviews, documentation and delivery."],
              ["03", "Professional Habits", "Build communication, ownership, deadlines, team workflow and consistency."],
              ["04", "Interview Story", "Turn project work into strong resume points and confident technical explanations."],
              ["05", "Opportunity Push", "Apply actively while improving your profile instead of waiting for experience to magically appear."],
            ].map(([number, title, text]) => (
              <article className="iep-momentum-card" key={number}>
                <span>{number}</span>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>


      {/* =====================================================
          IS THIS YOU
      ===================================================== */}
      <section className="iep-section">

        <div className="iep-container">

          <div className="iep-heading">

            <p className="iep-kicker">
              DOES THIS SOUND LIKE YOU?
            </p>

            <h2>
              You studied. You learned.
              <span> But your career still hasn't started properly.</span>
            </h2>

            <p>
              This program is designed specifically for candidates who already
              have technical knowledge but are struggling to convert those
              skills into strong career opportunities.
            </p>

          </div>


          <div className="iep-situation-grid">

            {[
              "Graduation is completed",
              "You already completed technical courses",
              "You know coding, AI, Data Science or Full Stack",
              "You have certificates and academic projects",
              "You are applying for jobs regularly",
              "Interview calls are fewer than expected",
              "Good opportunities are difficult to reach",
              "Companies repeatedly ask for experience",
              "Months are passing while searching for jobs",
              "Your resume gap is gradually increasing",
            ].map((item, index) => (
              <div className="iep-situation-card" key={item}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <p>{item}</p>
              </div>
            ))}

          </div>


          <div className="iep-highlight-statement">
            <strong>
              You may not need another course.
            </strong>
            <span>
              You may need a professional environment where your existing
              knowledge becomes real working experience.
            </span>
          </div>

        </div>

      </section>


      {/* =====================================================
          RESUME GAP
      ===================================================== */}
      <section className="iep-section iep-resume-section">

        <div className="iep-container iep-two-column">

          <div>

            <p className="iep-kicker">
              THE SILENT CAREER PROBLEM
            </p>

            <h2 className="iep-left-title">
              Skills are there.
              <span> But months keep disappearing.</span>
            </h2>

            <p className="iep-body-text">
              Many graduates believe that once they learn enough technologies,
              the right job will automatically arrive.
            </p>

            <p className="iep-body-text">
              But sometimes weeks become months — and months become a year.
              During that time the candidate may be learning, applying and
              trying hard, but the resume can still show very little
              professional experience.
            </p>

            <p className="iep-body-text">
              That growing gap can make the next opportunity even more
              difficult because companies may start asking:
              <strong> “What were you working on during this period?”</strong>
            </p>

          </div>


          <div className="iep-gap-card">

            <div className="iep-gap-number">
              01 → 03 → 06 → 12
            </div>

            <h3>
              Months Without Industry Exposure
            </h3>

            <p>
              Instead of allowing valuable months to become only
              “job-searching time”, this program is designed to turn that
              period into structured work, projects, preparation and
              measurable professional development.
            </p>

            <div className="iep-gap-bottom">
              Build experience while searching for the right opportunity.
            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          EXPERIENCE IS MORE THAN CODING
      ===================================================== */}
      <section className="iep-section">

        <div className="iep-container">

          <div className="iep-heading">

            <p className="iep-kicker">
              EXPERIENCE MATTERS
            </p>

            <h2>
              Industry experience is
              <span> much bigger than knowing how to code.</span>
            </h2>

            <p>
              You may hear people say that experience does not matter if your
              skills are strong. Skills absolutely matter — but professional
              work demands much more than technical knowledge alone.
            </p>

          </div>


          <div className="iep-professional-grid">

            {[
              ["Responsibility", "Owning your assigned work and being accountable for the final result."],
              ["Discipline", "Showing up consistently and delivering even when the work becomes difficult."],
              ["Deadlines", "Completing work within realistic timelines instead of unlimited practice time."],
              ["Teamwork", "Working with developers, mentors and teammates instead of solving everything alone."],
              ["Communication", "Explaining blockers, giving updates, asking questions and presenting your work."],
              ["Problem Solving", "Handling unexpected issues when the exact solution is not already provided."],
              ["Code Quality", "Writing maintainable work that another team member can understand and continue."],
              ["Documentation", "Recording requirements, decisions, implementation details and project knowledge."],
              ["Pressure Handling", "Learning to stay calm when priorities, bugs or requirements change."],
              ["Consistency", "Building professional habits through repeated daily execution."],
              ["Ownership", "Thinking beyond your assigned line of code and understanding the outcome."],
              ["Professional Behaviour", "Learning how real teams coordinate, review, report and deliver."],
            ].map(([title, text], index) => (
              <div className="iep-professional-card" key={title}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            ))}

          </div>

        </div>

      </section>


      {/* =====================================================
          JOURNEY IMAGE
      ===================================================== */}
      <section className="iep-section iep-journey-section">

        <div className="iep-container">

          <div className="iep-heading">
            <p className="iep-kicker">
              TWO POSSIBLE JOURNEYS
            </p>

            <h2>
              Don't stay isolated.
              <span> Move forward with direction.</span>
            </h2>
          </div>

          <div className="iep-journey-image-wrap">
            <img
              src="/industry-experience-journey.png"
              alt="Career journey comparison showing growth through industry experience"
              className="iep-journey-image"
            />
          </div>

        </div>

      </section>


      {/* =====================================================
          WHAT IS PROGRAM
      ===================================================== */}
      <section className="iep-section">

        <div className="iep-container iep-program-intro">

          <p className="iep-kicker">
            WHAT EXACTLY IS THIS?
          </p>

          <h2>
            Not Another Course.
            <span> A Career Bridge.</span>
          </h2>

          <p>
            The StudyAIPlacement Industry Experience Program is designed for
            candidates who already possess technical knowledge and now need
            structured professional exposure, real project execution and
            career momentum.
          </p>


          <div className="iep-program-stats">

            <div>
              <strong>Up to 12 Months</strong>
              <span>Maximum duration</span>
            </div>

            <div>
              <strong>8 Hours / Day</strong>
              <span>Structured career routine</span>
            </div>

            <div>
              <strong>Real Projects</strong>
              <span>Individual + team work</span>
            </div>

            <div>
              <strong>Exit Anytime</strong>
              <span>Once you secure a suitable opportunity</span>
            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          DAILY 8 HOURS
      ===================================================== */}
      <section className="iep-section iep-daily-section">

        <div className="iep-container">

          <div className="iep-heading">

            <p className="iep-kicker">
              YOUR DAILY CAREER ROUTINE
            </p>

            <h2>
              8 Hours.
              <span> One Clear Career Mission.</span>
            </h2>

          </div>


          <div className="iep-hours-grid">

            <div className="iep-hour-card iep-hour-main">

              <span className="iep-hours">4 HOURS</span>

              <h3>
                Real Industry Project Work
              </h3>

              <p>
                Work on assigned project requirements, coding, debugging,
                deployment, documentation, collaboration, reviews and
                deadlines.
              </p>

              <div className="iep-tags">
                <span>Tasks</span>
                <span>GitHub</span>
                <span>Deadlines</span>
                <span>Reviews</span>
                <span>Delivery</span>
              </div>

            </div>


            <div className="iep-hour-card">

              <span className="iep-hours">2 HOURS</span>

              <h3>
                Revision & Interview Preparation
              </h3>

              <p>
                Strengthen fundamentals, revise technologies, practise project
                explanations, technical interviews, HR rounds and professional
                communication.
              </p>

            </div>


            <div className="iep-hour-card">

              <span className="iep-hours">2 HOURS</span>

              <h3>
                Find & Pursue Opportunities
              </h3>

              <p>
                Search suitable openings, analyse requirements, improve
                applications, follow up, prepare for companies and continuously
                move toward interviews.
              </p>

            </div>

          </div>


          <div className="iep-big-message">
            <span>Instead of waiting for a job,</span>
            <strong>
              you spend every day becoming more employable while actively
              searching for one.
            </strong>
          </div>

        </div>

      </section>


      {/* =====================================================
          WORK ENVIRONMENT
      ===================================================== */}
      <section className="iep-section">

        <div className="iep-container">

          <div className="iep-heading">

            <p className="iep-kicker">
              WORK LIKE A PROFESSIONAL
            </p>

            <h2>
              Experience the environment
              <span> before entering the company.</span>
            </h2>

          </div>


          <div className="iep-work-grid">

            {[
              "Daily stand-ups",
              "Assigned responsibilities",
              "Individual projects",
              "Team projects",
              "Deadlines",
              "Git & GitHub workflow",
              "Code reviews",
              "Documentation",
              "Project meetings",
              "Task tracking",
              "Debugging",
              "Deployment",
              "Weekly reviews",
              "Performance feedback",
              "Professional communication",
              "Project ownership",
            ].map((item) => (
              <div className="iep-work-item" key={item}>
                <span>✓</span>
                {item}
              </div>
            ))}

          </div>

        </div>

      </section>


      {/* =====================================================
          PROJECTS
      ===================================================== */}
      <section className="iep-section iep-project-section">

        <div className="iep-container">

          <div className="iep-heading">

            <p className="iep-kicker">
              REAL PROJECT EXPOSURE
            </p>

            <h2>
              Stop collecting certificates.
              <span> Start building proof of work.</span>
            </h2>

          </div>


          <div className="iep-project-grid">

            {[
              "Artificial Intelligence",
              "Machine Learning",
              "Data Science",
              "Generative AI & LLMs",
              "AI Agents",
              "Computer Vision",
              "NLP",
              "Full Stack Development",
              "Backend & APIs",
              "Automation",
              "Deployment",
              "Internal / Industry Problem Statements",
            ].map((project) => (
              <div className="iep-project-card" key={project}>
                {project}
              </div>
            ))}

          </div>

          <p className="iep-note">
            Project allocation may be individual or team-based and depends on
            candidate skills, project requirements and available work.
          </p>

        </div>

      </section>


      {/* =====================================================
          PROOF OF EXPERIENCE
      ===================================================== */}
      <section className="iep-section">

        <div className="iep-container iep-two-column">

          <div>

            <p className="iep-kicker">
              BUILD VERIFIABLE EXPERIENCE
            </p>

            <h2 className="iep-left-title">
              Your work should leave
              <span> visible professional proof.</span>
            </h2>

            <p className="iep-body-text">
              Candidates are expected to build a genuine history of work,
              contribution and professional development throughout the
              program.
            </p>

          </div>


          <div className="iep-proof-list">

            <div>Experience / Engagement Documentation</div>
            <div>Project Contribution Record</div>
            <div>Technologies & Responsibilities</div>
            <div>GitHub / Portfolio Proof</div>
            <div>Performance Feedback</div>
            <div>Recommendation Letter for Eligible Performers</div>

          </div>

        </div>


        <div className="iep-container">

          <div className="iep-trust-message">
            <strong>
              Experience letters are not sold.
            </strong>

            <p>
              Documentation and recommendations are based on actual
              participation, contribution, discipline and performance.
            </p>
          </div>

        </div>

      </section>


      {/* =====================================================
          MONTHLY REVIEW
      ===================================================== */}
      <section className="iep-section iep-review-section">

        <div className="iep-container">

          <div className="iep-heading">

            <p className="iep-kicker">
              MONTHLY CAREER REVIEW
            </p>

            <h2>
              We don't want you to stay for one year.
              <span> We want you to become ready to leave.</span>
            </h2>

          </div>


          <div className="iep-review-grid">

            {[
              "Technical Progress",
              "Project Contribution",
              "Attendance & Discipline",
              "Communication",
              "Interview Readiness",
              "Resume & Profile",
              "Applications",
              "Interview Conversion",
            ].map((item) => (
              <div key={item}>{item}</div>
            ))}

          </div>


          <div className="iep-exit-box">

            <strong>
              Got a strong opportunity in Month 1?
            </strong>

            <span>
              Great. Move ahead.
            </span>

            <p>
              The program duration is maximum 12 months — not compulsory
              12 months. If you secure the right opportunity earlier, the
              purpose of the program is achieved.
            </p>

          </div>

        </div>

      </section>


      {/* =====================================================
          OPPORTUNITIES
      ===================================================== */}
      <section className="iep-section">

        <div className="iep-container">

          <div className="iep-heading">

            <p className="iep-kicker">
              EXPERIENCE + OPPORTUNITIES
            </p>

            <h2>
              Build experience and
              <span> search simultaneously.</span>
            </h2>

          </div>


          <div className="iep-opportunity-flow">

            <div>
              <span>01</span>
              <strong>Build Industry Experience</strong>
            </div>

            <b>→</b>

            <div>
              <span>02</span>
              <strong>Strengthen Professional Profile</strong>
            </div>

            <b>→</b>

            <div>
              <span>03</span>
              <strong>Interview & Apply</strong>
            </div>

            <b>→</b>

            <div>
              <span>04</span>
              <strong>Explore India + Global Opportunities</strong>
            </div>

          </div>

          <p className="iep-center-note">
            Eligible candidates can also explore the StudyAIPlacement Global
            Talent ecosystem for suitable international opportunities.
          </p>

        </div>

      </section>


      {/* =====================================================
          ELIGIBILITY
      ===================================================== */}
      <section className="iep-section iep-eligibility-section">

        <div className="iep-container">

          <div className="iep-heading">

            <p className="iep-kicker">
              WHO SHOULD JOIN?
            </p>

            <h2>
              This path is for
              <span> serious candidates only.</span>
            </h2>

          </div>


          <div className="iep-eligibility-grid">

            <div className="iep-eligible good">

              <h3>
                Good Fit ✓
              </h3>

              <ul>
                <li>Graduate or career-ready candidate</li>
                <li>Already knows coding / technical fundamentals</li>
                <li>Has completed some structured learning</li>
                <li>Actively looking for career opportunities</li>
                <li>Can commit to serious daily work</li>
                <li>Ready for deadlines and accountability</li>
                <li>Wants genuine industry exposure</li>
              </ul>

            </div>


            <div className="iep-eligible bad">

              <h3>
                Not the Right Fit ✕
              </h3>

              <ul>
                <li>Starting coding completely from zero</li>
                <li>Only wants an experience certificate</li>
                <li>Cannot maintain daily consistency</li>
                <li>Does not want project responsibility</li>
                <li>Only wants recorded lectures</li>
                <li>Does not want reviews or deadlines</li>
                <li>Is not serious about career preparation</li>
              </ul>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          ADMISSION PROCESS
      ===================================================== */}
      <section className="iep-section">

        <div className="iep-container">

          <div className="iep-heading">

            <p className="iep-kicker">
              ADMISSION PROCESS
            </p>

            <h2>
              We first check whether
              <span> this path is right for you.</span>
            </h2>

          </div>


          <div className="iep-process">

            {[
              ["01", "Apply", "Share your background and current skills."],
              ["02", "Skill Assessment", "Understand your current technical level."],
              ["03", "Discussion", "Review goals, commitment and career situation."],
              ["04", "Project Fit", "Identify suitable technology and project exposure."],
              ["05", "Admission", "Begin the structured industry experience journey."],
            ].map(([number, title, text]) => (
              <div className="iep-process-step" key={number}>
                <span>{number}</span>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            ))}

          </div>

        </div>

      </section>


      {/* =====================================================
          PRICE
      ===================================================== */}
      <section className="iep-section iep-price-section">

        <div className="iep-container">

          <div className="iep-price-card">

            <p className="iep-kicker">
              PROGRAM INVESTMENT
            </p>

            <h2>
              ₹20,000
            </h2>

            <span className="iep-tax">
              + applicable taxes
            </span>

            <p className="iep-price-description">
              For the complete Industry Experience Program journey,
              up to a maximum duration of 12 months.
            </p>


            <div className="iep-price-items">

              <span>Real Project Environment</span>
              <span>Professional Work Exposure</span>
              <span>Performance Reviews</span>
              <span>Interview Preparation</span>
              <span>Opportunity Guidance</span>
              <span>Experience Documentation</span>

            </div>


            <div className="iep-price-warning">
              The fee does not purchase an experience certificate.
              Experience documentation reflects actual work performed.
            </div>


            

          </div>

        </div>

      </section>


      {/* =====================================================
          THREE PATHS
      ===================================================== */}
      <section className="iep-section">

        <div className="iep-container">

          <div className="iep-heading">

            <p className="iep-kicker">
              ONE PLATFORM. THREE CAREER PATHS.
            </p>

            <h2>
              Start from
              <span> where you are today.</span>
            </h2>

          </div>


          <div className="iep-three-paths">

            <div>
              <span>NO SKILLS YET?</span>
              <h3>Learn & Build Skills</h3>
              <p>Join the AI Career Program.</p>
            </div>

            <div className="active">
              <span>SKILLS BUT NO EXPERIENCE?</span>
              <h3>Build Industry Experience</h3>
              <p>You are here.</p>
            </div>

            <div>
              <span>ALREADY EXPERIENCED?</span>
              <h3>Global Opportunities</h3>
              <p>Explore the Global Talent Network.</p>
            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          FAQ
      ===================================================== */}
      <section className="iep-section iep-faq-section">

        <div className="iep-container">

          <div className="iep-heading">

            <p className="iep-kicker">
              FREQUENTLY ASKED QUESTIONS
            </p>

            <h2>
              Understand the program
              <span> before you apply.</span>
            </h2>

          </div>


          <div className="iep-faq-grid">

            {[
              ["Is this another course?", "No. It is designed as a structured industry experience and career preparation environment for candidates who already possess technical knowledge."],

              ["Do I get an experience letter?", "Experience-related documentation is based on actual participation, project contribution and performance."],

              ["Will everyone receive a recommendation letter?", "No. Recommendation support should be performance-based and provided to eligible candidates."],

              ["Is placement guaranteed?", "No career outcome should be treated as automatic. The program focuses on experience, preparation, applications and improving access to suitable opportunities."],

              ["What if I get a job after one month?", "You can move ahead. Twelve months is the maximum duration, not a mandatory duration."],

              ["Will I work alone or in a team?", "Both may be possible depending on the project, candidate skill level and project requirements."],

              ["Why 8 hours?", "The objective is to build a professional routine that combines project execution, revision, interview preparation and active opportunity search."],

              ["Can I explore global opportunities later?", "Eligible candidates can also explore the StudyAIPlacement Global Talent pathway for suitable opportunities."],
            ].map(([question, answer]) => (
              <div className="iep-faq-card" key={question}>
                <h3>{question}</h3>
                <p>{answer}</p>
              </div>
            ))}

          </div>

        </div>

      </section>


      {/* =====================================================
          FINAL CTA
      ===================================================== */}
      <section className="iep-final-cta">

        <div className="iep-container iep-final-box">

          <p className="iep-kicker">
            YOUR NEXT MONTH CAN LOOK DIFFERENT
          </p>

          <h2>
            You already spent time learning.
            <span> Now start building the experience companies expect.</span>
          </h2>

          <p>
            Don't spend the next several months only waiting for interview
            calls. Build, contribute, prepare, apply and move forward with a
            serious professional routine.
          </p>

          

        </div>

      </section>

    </main>
  );
};

export default IndustryExperience;
